import type { AuditFilters } from "@/stores/audit";

const STORAGE_KEY = "opus.audit.saved-searches";
const MAX_ENTRIES = 12;

export interface SavedAuditSearch {
	id: string;
	name: string;
	createdAt: string;
	filters: AuditFilters;
}

function sanitizeFilters(filters: AuditFilters): AuditFilters {
	return {
		query: filters.query ?? "",
		kinds: Array.isArray(filters.kinds) ? filters.kinds : [],
		vendors: Array.isArray(filters.vendors) ? filters.vendors : [],
	};
}

function readRaw(): SavedAuditSearch[] {
	if (typeof window === "undefined") {
		return [];
	}

	const raw = window.localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return [];
	}

	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed
			.filter((entry): entry is SavedAuditSearch => typeof entry === "object" && entry !== null)
			.map((entry) => ({
				id: typeof entry.id === "string" ? entry.id : crypto.randomUUID(),
				name: typeof entry.name === "string" ? entry.name : "Saved search",
				createdAt: typeof entry.createdAt === "string" ? entry.createdAt : new Date().toISOString(),
				filters: sanitizeFilters((entry.filters ?? {}) as AuditFilters),
			}));
	} catch {
		return [];
	}
}

function writeRaw(searches: SavedAuditSearch[]) {
	if (typeof window === "undefined") {
		return searches;
	}
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
	return searches;
}

export function loadSavedAuditSearches() {
	return readRaw().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function addSavedAuditSearch(name: string, filters: AuditFilters) {
	const next: SavedAuditSearch = {
		id: crypto.randomUUID(),
		name: name.trim(),
		createdAt: new Date().toISOString(),
		filters: sanitizeFilters(filters),
	};

	const searches = [next, ...loadSavedAuditSearches()].slice(0, MAX_ENTRIES);
	return writeRaw(searches);
}

export function removeSavedAuditSearch(id: string) {
	return writeRaw(loadSavedAuditSearches().filter((entry) => entry.id !== id));
}
